import { type ReactNode, useEffect, useState } from "react";
import type { HighlightLang } from "./lezer-highlighter";
import styles from "./YamlView.module.css";

/**
 * Read-only, syntax-highlighted source pane, shared by the template detail
 * pages (YAML) and the Files preview pane (JSON/YAML/Python). Consumption
 * pages have no editor (owner decision 2026-07-19), so this renders text and
 * nothing else — no textarea, no contenteditable, no mounted editor view.
 *
 * The Lezer highlighter rides its own lazy chunk. Until it resolves — and if
 * highlighting ever throws — the identically styled plain <pre> renders the
 * raw source, so the pane is never blank and never crashes the page.
 */
export function YamlView({
  value,
  ariaLabel,
  height = 460,
  dark = false,
  lang = "yaml",
  className,
}: {
  value: string;
  ariaLabel: string;
  /**
   * Max height of the scroll container, in px. `null` emits no inline cap so
   * the caller's CSS sizes the pane (the Files preview flex-fills its column).
   */
  height?: number | null;
  /** Dark pane skin + dark token palette (the template detail card). */
  dark?: boolean;
  /** Highlight grammar; the template pages keep the YAML default. */
  lang?: HighlightLang;
  /**
   * Replaces the template-page pane skin so the Files preview can supply its
   * own (avoids two same-specificity CSS modules fighting over the box). The
   * token palette class is always applied regardless — a custom skin changes
   * the frame, never the highlighting. The label / tab-stop / plain-<pre>-
   * fallback contract is unchanged.
   */
  className?: string | undefined;
}) {
  const [nodes, setNodes] = useState<ReactNode | null>(null);

  useEffect(() => {
    let cancelled = false;
    setNodes(null);
    void import("./lezer-highlighter")
      .then(({ highlightSource }) => {
        const next = highlightSource(value, lang);
        if (!cancelled) setNodes(next);
      })
      .catch(() => {
        if (!cancelled) setNodes(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value, lang]);

  // A labelled <section> is the region the source is announced as; it scrolls,
  // so it also needs its own tab stop to stay keyboard-reachable.
  const paneSkin = className ?? `${styles.pane} ${dark ? styles.dark : styles.light}`;
  const paneProps = {
    className: `${paneSkin} ${dark ? styles.tokensDark : styles.tokensLight}`,
    "aria-label": ariaLabel,
    tabIndex: 0,
    ...(height === null ? {} : { style: { maxHeight: `${height}px` } }),
  };

  if (nodes === null) {
    return (
      <section {...paneProps}>
        <pre>{value}</pre>
      </section>
    );
  }
  return (
    <section {...paneProps}>
      <pre data-highlighted="true">{nodes}</pre>
    </section>
  );
}
